/*
 * @Author: Ryan
 * @Date: 2026-02-04
 * @Description: Testimonials section - What users say about FalcoCut TikTok Video Generator
 */
import React from 'react';

interface Testimonial {
  title: string;
  content: string;
  role: string;
  name: string;
}

const testimonials: Testimonial[] = [
  {
    title: 'Changed How I Create Content',
    content:
      "I used to spend an entire day making one TikTok. Now I generate 5-10 videos in an afternoon. The AI understands TikTok's style — the pacing, captions, and music all feel native. My engagement has gone up since I started posting more consistently.",
    role: 'Content Creator',
    name: 'Jessica Park',
  },
  {
    title: 'Perfect for Product Ads',
    content:
      'We paste our product links and get polished UGC-style ads in minutes. Testing multiple creatives used to cost us thousands in freelancer fees. Now we iterate on ad variations in-house and launch campaigns faster than ever.',
    role: 'E-Commerce Marketer',
    name: 'Ryan Torres',
  },
  {
    title: 'Scaled Our Agency Output',
    content:
      "We handle 20+ clients and FalcoCut lets us produce branded short-form content without hiring more editors. The quality is consistent and clients love the turnaround time. It's become a core part of our workflow.",
    role: 'Creative Director',
    name: 'Aisha Patel',
  },
  {
    title: 'No Camera, No Problem',
    content:
      "I don't like being on camera, so the AI avatar and voiceover features are a game-changer. I write my scripts, pick a voice, and get a polished video without ever filming myself. My TikTok channel has grown 3x since I started using it.",
    role: 'Side Hustle Creator',
    name: 'Marcus Chen',
  },
  {
    title: 'Repurposing Made Easy',
    content:
      "I take my podcast episodes, drop them into FalcoCut, and get 10-15 short clips ready for TikTok and Reels. It picks the best moments, adds captions, and formats everything vertically. It's saved me hours every week.",
    role: 'Podcast Host',
    name: 'Laura Bennett',
  },
  {
    title: 'Finally, a Tool That Gets TikTok',
    content:
      "Other tools make generic videos. FalcoCut actually understands what works on TikTok — the quick cuts, the text overlays, the hook-driven structure. It's not just a video editor, it's a TikTok content engine.",
    role: 'Social Media Manager',
    name: 'David Kim',
  },
];

const StarRating: React.FC = () => (
  <div className="flex gap-1 mb-3 pad:mb-4">
    {[1, 2, 3, 4, 5].map((star) => (
      <svg
        key={star}
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M10 1L12.39 6.26L18 7.27L14 11.14L14.76 17L10 14.27L5.24 17L6 11.14L2 7.27L7.61 6.26L10 1Z"
          fill="#FBBF24"
          stroke="#FBBF24"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ))}
  </div>
);

const TestimonialCard: React.FC<Testimonial> = ({ title, content, role, name }) => {
  return (
    <div className="bg-[rgba(255,255,255,0.055)] rounded-2xl p-6 pad:p-8 h-full flex flex-col">
      <StarRating />
      <h3 className="text-lg pad:text-xl font-bold text-white mb-3 pad:mb-4">{title}</h3>
      <p className="text-sm pad:text-base text-white/65 leading-relaxed flex-grow mb-4 pad:mb-6">
        {content}
      </p>
      <div>
        <p className="font-semibold text-white text-sm pad:text-base">{role}</p>
        <p className="text-white/50 text-sm">{name}</p>
      </div>
    </div>
  );
};

const TestimonialsSection: React.FC = () => {
  return (
    <section className="w-full bg-black py-[60px] pad:py-[80px] sm:py-[100px] px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        {/* Title */}
        <h2 className="text-2xl pad:text-3xl sm:text-[42px] font-bold text-center text-white mb-10 pad:mb-14 sm:mb-16 leading-tight">
          What Our Customers Say
        </h2>

        {/* Testimonials Grid */}
        <div className="grid grid-cols-1 pad:grid-cols-2 sm:grid-cols-3 gap-4 pad:gap-6">
          {testimonials.map((testimonial, index) => (
            <TestimonialCard
              key={index}
              title={testimonial.title}
              content={testimonial.content}
              role={testimonial.role}
              name={testimonial.name}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
